/* Queenslander Aviaries - Wishlist
 * Stores saved products in localStorage, fills the heart over the product
 * image, and renders a slide-out tray using the theme's side-drawer element.
 * Also reports saves to Klaviyo so wishlist data can be used in flows.
 * Scoped to [data-qa-wl*] selectors; touches nothing else.
 */
(function () {
  'use strict';

  var KEY = 'qa-wishlist';
  var DRAWER_ID = 'qa-wishlist-drawer';

  /* ---------- Klaviyo ---------- */

  /* Pushes to Klaviyo's onsite queue if it is present. Silent no-op if the
     Klaviyo embed is not loaded, so nothing here can break the wishlist.
     Only identified profiles (subscribed, or arrived from a Klaviyo email)
     can actually be emailed; anonymous browsers are still tracked. */
  function kl(args) {
    try {
      var q = window.klaviyo || window._learnq;
      if (q && typeof q.push === 'function') q.push(args);
    } catch (e) { /* Klaviyo absent or blocked */ }
  }

  /* Rewrites the WHOLE list on every change rather than appending, so that
     removals sync too. Otherwise customers get "back in stock" emails for
     things they took off the wishlist weeks ago. */
  function syncProfile(items) {
    var ids = items.map(function (i) { return String(i.p); });
    var titles = items.map(function (i) { return i.t || ''; }).filter(Boolean);

    kl(['identify', {
      'Wishlist Product IDs': ids,
      'Wishlist Product Titles': titles,
      'Wishlist Count': ids.length,
      'Wishlist Last Updated': new Date().toISOString()
    }]);
  }

  /* ---------- Storage ---------- */

  function read() {
    try {
      var raw = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(raw) ? raw.filter(function (i) { return i && i.p; }) : [];
    } catch (e) {
      return [];
    }
  }

  function write(items) {
    try {
      localStorage.setItem(KEY, JSON.stringify(items.slice(0, 50)));
    } catch (e) { /* storage full or blocked */ }
    syncProfile(items);
    document.dispatchEvent(new CustomEvent('qa:wishlist:change', { bubbles: true }));
  }

  function has(productId) {
    return read().some(function (i) { return String(i.p) === String(productId); });
  }

  function remove(productId, title) {
    var items = read().filter(function (i) { return String(i.p) !== String(productId); });
    write(items);
    kl(['track', 'Removed from Wishlist', {
      ProductID: String(productId),
      ProductName: title || '',
      WishlistCount: items.length
    }]);
  }

  function add(data) {
    var items = read().filter(function (i) { return String(i.p) !== String(data.p); });
    items.unshift(data);
    write(items);

    kl(['track', 'Added to Wishlist', {
      ProductID: String(data.p),
      VariantID: String(data.v || ''),
      ProductName: data.t || '',
      Price: data.pr || '',
      URL: data.u || '',
      ImageURL: data.img || '',
      WishlistCount: items.length
    }]);
  }

  /* ---------- Product page button ---------- */

  function currentVariant(btn) {
    var scope = btn.closest('.cc-main-product') || document;
    var input = scope.querySelector('.js-product-form [name="id"]');
    return input ? input.value : btn.getAttribute('data-variant-id') || '';
  }

  function itemFromButton(btn) {
    return {
      p: btn.getAttribute('data-product-id'),
      v: currentVariant(btn),
      t: btn.getAttribute('data-product-title') || '',
      pr: btn.getAttribute('data-product-price') || '',
      u: btn.getAttribute('data-product-url') || '',
      img: btn.getAttribute('data-product-image') || ''
    };
  }

  /* Moves the floating heart into the media gallery so it sits over the
     top-left of the product image. */
  function placeFloatingButtons() {
    var btns = document.querySelectorAll('[data-qa-wl-toggle][data-qa-wl-float]');
    for (var i = 0; i < btns.length; i += 1) {
      var btn = btns[i];
      if (btn.getAttribute('data-qa-wl-placed') === 'true') continue;
      var scope = btn.closest('.cc-main-product') || document;
      var target = scope.querySelector('.media-gallery__viewer');
      if (!target) continue;
      target.appendChild(btn);
      btn.setAttribute('data-qa-wl-placed', 'true');
      btn.hidden = false;
    }
  }

  function paintButtons() {
    var btns = document.querySelectorAll('[data-qa-wl-toggle]');
    for (var i = 0; i < btns.length; i += 1) {
      var saved = has(btns[i].getAttribute('data-product-id'));
      btns[i].classList.toggle('is-saved', saved);
      btns[i].setAttribute('aria-pressed', saved ? 'true' : 'false');
      btns[i].setAttribute('title', saved ? 'Added to Wishlist' : 'Add to Wishlist');
      var label = btns[i].querySelector('[data-qa-wl-label]');
      if (label) {
        label.textContent = saved
          ? label.getAttribute('data-label-added')
          : label.getAttribute('data-label-add');
      }
    }
  }

  /* ---------- Drawer ---------- */

  function getDrawer() {
    return document.getElementById(DRAWER_ID);
  }

  function setState(drawer, state) {
    var wrap = drawer.querySelector('[data-qa-wl-body]');
    if (wrap) wrap.setAttribute('data-state', state);
  }

  /* The mobile menu, cart and wishlist are all drawers sharing one overlay.
     Opening ours while another is open leaves the overlay in a broken state,
     so close any others first. This is why the mobile menu link appeared to
     do nothing. */
  function closeOtherDrawers(self) {
    var open = document.querySelectorAll('side-drawer[open], cart-drawer[open], [data-name][open]');
    var closed = false;
    for (var i = 0; i < open.length; i += 1) {
      var el = open[i];
      if (el === self) continue;
      if (typeof el.close === 'function') {
        try {
          el.close();
          closed = true;
        } catch (e) { /* not a drawer */ }
      }
    }
    return closed;
  }

  function restoreVariants(list, items) {
    var map = {};
    items.forEach(function (i) { map[String(i.p)] = String(i.v || ''); });

    var rows = list.querySelectorAll('[data-qa-wl-row]');
    for (var i = 0; i < rows.length; i += 1) {
      var pid = rows[i].getAttribute('data-product-id');
      var select = rows[i].querySelector('[data-qa-wl-variant]');
      if (select && map[pid]) {
        var opt = select.querySelector('option[value="' + map[pid] + '"]');
        if (opt) select.value = map[pid];
      }
    }
  }

  function loadItems(drawer) {
    var items = read();
    var list = drawer.querySelector('[data-qa-wl-list]');
    if (!list) return;

    if (!items.length) {
      list.innerHTML = '';
      setState(drawer, 'empty');
      return;
    }

    setState(drawer, 'loading');

    var q = items.map(function (i) { return 'id:' + i.p; }).join(' OR ');
    var url = drawer.getAttribute('data-render-url') + '&q=' + encodeURIComponent(q);

    fetch(url)
      .then(function (res) {
        if (!res.ok) throw new Error(res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-qa-wl-list]');
        if (!fresh || !fresh.children.length) {
          list.innerHTML = '';
          setState(drawer, 'empty');
          return;
        }
        list.innerHTML = fresh.innerHTML;
        restoreVariants(list, items);
        setState(drawer, 'ready');
      })
      .catch(function (err) {
        console.warn('Wishlist could not load items:', err);
        setState(drawer, 'error');
      });
  }

  function openDrawer(opener) {
    var drawer = getDrawer();
    if (!drawer || typeof drawer.open !== 'function') return;

    var hadOpen = closeOtherDrawers(drawer);
    loadItems(drawer);
    kl(['track', 'Viewed Wishlist', { WishlistCount: read().length }]);

    if (hadOpen) {
      window.setTimeout(function () { drawer.open(opener); }, 250);
    } else {
      drawer.open(opener);
    }
  }

  function addToCart(row, btn) {
    var select = row.querySelector('[data-qa-wl-variant]');
    var id = select ? select.value : row.getAttribute('data-variant-id');
    if (!id) return;

    btn.classList.add('is-loading');
    btn.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
    })
      .then(function (res) {
        if (!res.ok) throw new Error(res.status);
        return res.json();
      })
      .then(function () {
        kl(['track', 'Added to Cart from Wishlist', { VariantID: String(id) }]);

        var wishlist = getDrawer();
        var cart = document.querySelector('cart-drawer');
        if (wishlist && typeof wishlist.close === 'function') wishlist.close();

        if (cart && typeof cart.refresh === 'function') {
          window.setTimeout(function () { cart.refresh(true); }, 250);
        } else {
          window.location.href = '/cart';
        }
      })
      .catch(function (err) {
        console.warn('Wishlist add to cart failed:', err);
      })
      .finally(function () {
        btn.classList.remove('is-loading');
        btn.disabled = false;
      });
  }

  /* ---------- Events ---------- */

  document.addEventListener('click', function (evt) {
    var toggle = evt.target.closest('[data-qa-wl-toggle]');
    if (toggle) {
      evt.preventDefault();
      var pid = toggle.getAttribute('data-product-id');
      if (has(pid)) {
        remove(pid, toggle.getAttribute('data-product-title'));
      } else {
        add(itemFromButton(toggle));
      }
      paintButtons();
      return;
    }

    var opener = evt.target.closest('a[href$="#open-wishlist"], [data-qa-wl-open]');
    if (opener) {
      evt.preventDefault();
      evt.stopPropagation();
      openDrawer(opener);
      return;
    }

    var removeBtn = evt.target.closest('[data-qa-wl-remove]');
    if (removeBtn) {
      evt.preventDefault();
      var row = removeBtn.closest('[data-qa-wl-row]');
      if (!row) return;
      remove(row.getAttribute('data-product-id'), row.getAttribute('data-product-title'));
      row.remove();
      paintButtons();
      var drawer = getDrawer();
      if (drawer && !drawer.querySelectorAll('[data-qa-wl-row]').length) {
        setState(drawer, 'empty');
      }
      return;
    }

    var cartBtn = evt.target.closest('[data-qa-wl-add]');
    if (cartBtn) {
      evt.preventDefault();
      var cartRow = cartBtn.closest('[data-qa-wl-row]');
      if (cartRow) addToCart(cartRow, cartBtn);
    }
  }, true);

  function boot() {
    placeFloatingButtons();
    paintButtons();
  }

  document.addEventListener('qa:wishlist:change', paintButtons);
  document.addEventListener('shopify:section:load', boot);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}());
